import type {
  CostCategory,
  CostClass,
  CostLineItem,
  CostLineItemPayload,
} from './types-cost';
import { HOLD_FLAG_OPTIONS } from './identification-utils';
import { formatNumber, formatDecimalCurrency } from 'utils/number';

// Small cost helpers ported from the mock `data/costs`. The structuring FE only
// needs currency formatting, the canonical category set (P4 — all 5 offered in
// the line-item Category dropdown), and the Net AFE Cost formula
// (grand_total × WI% / 100). Subtotals + tile rollups come from the BE; these
// helpers recompute optimistic display values during inline edits.

/** The 5 canonical cost categories (CostCategories set). */
export const COST_CATEGORIES = [
  'Drilling',
  'Completion',
  'Facilities',
  'Contingencies & Overhead',
  'Other',
] as const;

export type CostCategoryName = (typeof COST_CATEGORIES)[number];

/**
 * Per-category sub-category options — the FE mirror of the BE
 * `CostCategories::COST_CATEGORIES` constant (app/models/concerns/cost_categories.rb).
 * Kept in lock-step with the backend so the v2 edit-mode Sub-Category `Dropdown`
 * offers the same canonical list the BE buckets line items into. Categories not
 * in this map (none today) fall back to an empty option list.
 */
export const SUB_CATEGORIES_BY_CATEGORY: Record<string, readonly string[]> = {
  'Drilling': [
    'Drilling Rig',
    'Drilling Services',
    'Drilling Fluids',
    'Drilling Casing',
    'Drilling Consumables',
    'Drilling Support',
  ],
  'Completion': [
    'Stimulation',
    'Completion Rig',
    'Completion Services',
    'Completion Equipment',
    'Flowback',
    'Artificial Lift',
  ],
  'Facilities': [
    'Surface Equipment',
    'Production Equipment',
    'Pipeline Gathering',
    'Infrastructure',
  ],
  'Contingencies & Overhead': [
    'Contingency',
    'Overhead',
    'Insurance',
    'Permits/Legal',
    'Supervision',
  ],
  'Other': ['Other'],
};

/**
 * Sub-category options for a category (empty for unknown categories).
 * @param {string} category - The cost category name.
 * @return {string[]} The sub-category options for the category.
 */
export const subCategoryOptionsFor = (category: string): readonly string[] =>
  SUB_CATEGORIES_BY_CATEGORY[category] ?? [];

export const COST_CLASS_OPTIONS: CostClass[] = ['intangible', 'tangible'];

/** Sentinel label for "no class" in the v2 class Dropdown. */
export const CLASS_NONE_LABEL = '— none —';

/**
 * Display a numeric amount as 2-decimal USD; em-dash for nullish.
 * Reuses the shared 2-decimal formatter so cost columns/tiles preserve the cents
 * the backend serves (commas, `-$1,234.56`, `$0.00`). Distinct from the app-wide
 * whole-dollar formatter, which would round cents off these AFE cost values.
 * @param {number | null | undefined} value - The amount to format.
 * @return {string} The USD-formatted string, or an em-dash when nullish/NaN.
 */
export const formatCurrency = (value: number | null | undefined): string =>
  formatDecimalCurrency(value, '—');

/**
 * Working interest display — up to 4 dp, trailing zeros trimmed.
 * @param {number | null | undefined} value - The working interest value.
 * @return {string} The trimmed display string, or an em-dash when nullish/NaN.
 */
export const formatWorkingInterest = (
  value: number | null | undefined
): string => {
  if (value === null || value === undefined || Number.isNaN(value)) return '—';
  return `${Number(value.toFixed(4))}`;
};

/**
 * Working interest editor seed — always 5 decimals (zero-padded), e.g.
 * `12.5` → `"12.50000"`. Blank for null/NaN. Matches the v2 mock's WI input
 * value formatting (separate from the 4dp display helper above).
 * @param {number | null | undefined} value - The working interest value.
 * @return {string} The 5-decimal seed string, or '' when nullish/NaN.
 */
export const formatWorkingInterest5 = (
  value: number | null | undefined
): string => {
  return value === null || value === undefined || Number.isNaN(value)
    ? ''
    : formatNumber({
        value,
        minPrecision: 5,
        maxPrecision: 5,
        includeCommas: false,
        fallback: '',
      });
};

/**
 * Net AFE Cost = grand_total × working_interest_percentage / 100 (server formula).
 * @param {number} grandTotal - The AFE grand total.
 * @param {number | null | undefined} workingInterestPct - The working interest percentage.
 * @return {number | null} The net AFE cost, or null when WI is nullish.
 */
export const computeNetAfeCost = (
  grandTotal: number,
  workingInterestPct: number | null | undefined
): number | null => {
  if (workingInterestPct === null || workingInterestPct === undefined) {
    return null;
  }
  return Math.round((grandTotal * workingInterestPct) / 100);
};

/**
 * Display label for the Hold Flag field — an AfeHeader held_flag enum value
 * (held → "Held", submittal → "Submittal"). Blank / unknown renders an em-dash.
 * Reuses the HOLD_FLAG_OPTIONS vocab so the label set has a single FE source.
 * @param {string | null | undefined} value - The raw held_flag enum value.
 * @return {string} The display label, or an em-dash when blank/unknown.
 */
export const formatHoldFlag = (value: string | null | undefined): string => {
  if (value === null || value === undefined || value === '') return '—';
  const match = HOLD_FLAG_OPTIONS.find((o) => o.value === value);
  return match && match.value !== '' ? match.label : '—';
};

/**
 * Human label for a cost class enum value.
 * @param {CostClass | null | undefined} value - The cost class enum value.
 * @return {string} The human label, or an em-dash when nullish/unknown.
 */
export const costClassLabel = (value: CostClass | null | undefined): string => {
  if (value === 'intangible') return 'Intangible';
  if (value === 'tangible') return 'Tangible';
  return '—';
};

/**
 * Bucket sort order for cost class within a category: intangible → tangible →
 * unclassed (null). Mirrors the mock CLASS_ORDER (Intangible / Tangible / null).
 */
export const CLASS_RANK: ReadonlyArray<CostClass | null> = [
  'intangible',
  'tangible',
  null,
];

/** The minimal shape deriveCostBuckets needs off a (draft) cost line item. */
export interface BucketableItem {
  category: string;
  costClass: CostClass | null;
  classCode: string | null;
}

/** A renderable cost bucket grouped by (category, class). */
export interface CostBucket<T extends BucketableItem = BucketableItem> {
  id: string;
  name: string;
  category: string;
  costClass: CostClass | null;
  /** Shared class code chip — set only when ALL members agree on one non-empty code. */
  classCode: string | null;
  items: T[];
  subtotal: number;
}

/**
 * Group flat (draft) line items into renderable buckets by (category, class),
 * ported from the mock `deriveBuckets`. Bucket id is `${category}|${costClass ?? ''}`;
 * name is `${classLabel} ${category}` (e.g. "Intangible Drilling") when classed,
 * else the bare category. Each bucket's `classCode` chip is resolved from the
 * shared item-level classCode ONLY when every member agrees on one non-empty
 * value. Subtotals use the caller-supplied `costOf` (revisedTotal ?? cost*qty,
 * matching the BE effective_total SSOT). Sorted by COST_CATEGORIES rank then
 * CLASS_RANK.
 * @param {T[]} items - The flat (draft) line items to group.
 * @param {function(T): number} costOf - Resolves each item's subtotal contribution.
 * @return {Array<CostBucket>} The grouped, sorted cost buckets.
 */
export const deriveCostBuckets = <T extends BucketableItem>(
  items: T[],
  costOf: (item: T) => number
): CostBucket<T>[] => {
  const map = new Map<string, CostBucket<T>>();

  items.forEach((item) => {
    const id = `${item.category}|${item.costClass ?? ''}`;
    let bucket = map.get(id);
    if (!bucket) {
      bucket = {
        id,
        name: item.costClass
          ? `${costClassLabel(item.costClass)} ${item.category}`
          : item.category,
        category: item.category,
        costClass: item.costClass,
        classCode: null,
        items: [],
        subtotal: 0,
      };
      map.set(id, bucket);
    }
    bucket.items.push(item);
    bucket.subtotal += costOf(item);
  });

  // Resolve the shared classCode chip per bucket — set it only when EVERY member
  // carries the same non-empty code. `presentCodes` keeps duplicates so the
  // "every item has a code" check compares against the item count BEFORE the Set
  // dedup collapses agreeing codes (the prior `codes.size === items.length` guard
  // only ever held for a single-member bucket, so multi-item buckets never showed
  // the chip even when all their codes agreed).
  map.forEach((bucket) => {
    const presentCodes = bucket.items
      .map((i) => i.classCode?.trim())
      .filter((c): c is string => !!c);
    const distinctCodes = new Set(presentCodes);
    bucket.classCode =
      distinctCodes.size === 1 && presentCodes.length === bucket.items.length
        ? presentCodes[0]
        : null;
  });

  const categoryRank = new Map<string, number>(
    COST_CATEGORIES.map((c, i) => [c, i])
  );
  const classRank = new Map<CostClass | null, number>(
    CLASS_RANK.map((c, i) => [c, i])
  );
  return Array.from(map.values()).sort((a, b) => {
    const ca = categoryRank.get(a.category) ?? 999;
    const cb = categoryRank.get(b.category) ?? 999;
    if (ca !== cb) return ca - cb;
    const la = classRank.get(a.costClass) ?? 999;
    const lb = classRank.get(b.costClass) ?? 999;
    return la - lb;
  });
};

// ---------------------------------------------------------------------------
// Draft line-item model + pure draft helpers (extracted from structure-costs).
// These carry no React state — they convert the server set into the editable
// draft, diff the draft back into the PATCH payload, and supply the shared
// label/option/header constants used by both the read-view and the editor.
// ---------------------------------------------------------------------------

// A draft line item carries a client-only key + a dirty flag so Save can
// diff against the server set (updates / creates / destroys). New rows have a
// null id placeholder we strip when building the payload.
export interface DraftLineItem {
  key: string;
  /** Server id (positive) or null for a newly-added row. */
  id: number | null;
  code: string | null;
  description: string | null;
  costClass: CostClass | null;
  category: string;
  subCategory: string | null;
  /** Operator's class code (e.g. "0180"). Persists to its own column. */
  classCode: string | null;
  costPerUnit: number;
  quantity: number;
  /** Reviewer-set revised line total; null falls back to costPerUnit * quantity. */
  revisedTotal: number | null;
  /** System-managed balancing row (see types-cost). Editing it promotes it to a normal row on the BE. */
  systemGenerated: boolean;
  isNew: boolean;
  destroyed: boolean;
}

export interface DraftCategory {
  category: string;
  code: string | null;
}

export const CLASS_DROPDOWN_ITEMS: Array<CostClass | typeof CLASS_NONE_LABEL> =
  ['intangible', 'tangible', CLASS_NONE_LABEL];

/**
 * Class dropdown label — like {@link costClassLabel} but returns the
 * CLASS_NONE_LABEL sentinel (not an em-dash) for the no-class case so the
 * value round-trips through the edit-mode class Dropdown.
 * @param {CostClass | string} value - The class value or CLASS_NONE_LABEL sentinel.
 * @return {string} The human label for the class dropdown.
 */
export const classLabel = (
  value: CostClass | typeof CLASS_NONE_LABEL
): string => {
  if (value === 'intangible') return 'Intangible';
  if (value === 'tangible') return 'Tangible';
  return CLASS_NONE_LABEL;
};

/**
 * Effective line total used in every rollup (grand total, category subtotal,
 * bucket subtotal). Mirrors the BE AfeCostItem#effective_total SSOT:
 * revisedTotal when set, else costPerUnit * quantity.
 * @param {DraftLineItem} item - The draft line item to value.
 * @return {number} The effective line total.
 */
export const lineCost = (item: DraftLineItem): number => {
  return item.revisedTotal !== null
    ? item.revisedTotal
    : item.costPerUnit * item.quantity;
};

export const SUB_CATEGORY_NONE = '—';

/**
 * Sub-category dropdown options for a draft row — the canonical options plus a
 * "none" sentinel, and the current value prepended when it is a free/legacy
 * value not in the canonical list.
 * @param {string} category - The row's category.
 * @param {string | null} current - The row's current sub-category.
 * @return {string[]} The dropdown option list.
 */
export const subCategoryDropdownItems = (
  category: string,
  current: string | null
): string[] => {
  const options = [...subCategoryOptionsFor(category)];
  const trimmed = current?.trim();
  if (trimmed && trimmed !== SUB_CATEGORY_NONE && !options.includes(trimmed)) {
    return [trimmed, SUB_CATEGORY_NONE, ...options];
  }
  return [SUB_CATEGORY_NONE, ...options];
};

export const COST_TABLE_HEADERS = [
  { key: 'code', header: 'Code' },
  { key: 'description', header: 'Description' },
  { key: 'costClass', header: 'Class' },
  { key: 'category', header: 'Category' },
  { key: 'subCategory', header: 'Sub-Category' },
  { key: 'originalCost', header: 'Original Cost', right: true },
  { key: 'totalCost', header: 'Total Cost', right: true },
];

let draftKeySeq = 0;
/**
 * Allocate a unique client-only key for a newly-added draft row.
 * @return {string} The unique draft key.
 */
export const nextKey = (): string => `draft-${(draftKeySeq += 1)}`;

/**
 * Convert the server category set into the flat editable draft list.
 * @param {CostCategory[]} categories - The server category set.
 * @return {DraftLineItem[]} The flat draft line items.
 */
export const toDraftItems = (categories: CostCategory[]): DraftLineItem[] =>
  categories.flatMap((cat) =>
    cat.lineItems.map((li) => ({
      key: `srv-${li.id}`,
      id: li.id,
      code: li.code,
      description: li.description,
      costClass: li.costClass,
      category: li.category ?? cat.category,
      subCategory: li.subCategory,
      classCode: li.classCode ?? null,
      costPerUnit: li.costPerUnit ?? 0,
      quantity: li.quantity ?? 1,
      revisedTotal: li.revisedTotal ?? null,
      systemGenerated: li.systemGenerated ?? false,
      isNew: false,
      destroyed: false,
    }))
  );

/**
 * Capture the server category metadata for the draft.
 * @param {CostCategory[]} categories - The server category set.
 * @return {DraftCategory[]} The draft category metadata.
 */
export const toDraftCategories = (
  categories: CostCategory[]
): DraftCategory[] =>
  categories.map((c) => ({ category: c.category, code: c.code }));

/**
 * Build the PATCH payload by diffing the draft against the original server set.
 * @param {DraftLineItem[]} draft - The current draft line items.
 * @param {CostLineItem[]} original - The original server-side line items.
 * @return {CostLineItemPayload[]} The diffed create/update/destroy payload.
 */
export const buildLineItemPayload = (
  draft: DraftLineItem[],
  original: CostLineItem[]
): CostLineItemPayload[] => {
  const originalById = new Map(original.map((li) => [li.id, li]));
  const payload: CostLineItemPayload[] = [];

  draft.forEach((d) => {
    if (d.isNew) {
      if (d.destroyed) return; // added then removed before save — skip
      payload.push({
        description: d.description,
        category: d.category,
        sub_category: d.subCategory,
        cost_class: d.costClass,
        cost_per_unit: d.costPerUnit,
        quantity: d.quantity,
        code: d.code,
        class_code: d.classCode,
        revised_total: d.revisedTotal,
      });
      return;
    }
    if (d.id === null) return;
    if (d.destroyed) {
      payload.push({ id: d.id, _destroy: true });
      return;
    }
    const orig = originalById.get(d.id);
    if (!orig) return;
    // `quantity` is excluded — AfeHeader only applies it on CREATE, so an
    // update carrying quantity would be silently dropped. `code` (extraction) and
    // `class_code` (user-set) now persist to their own columns and are diffed.
    const changed =
      (orig.costPerUnit ?? 0) !== d.costPerUnit ||
      (orig.costClass ?? null) !== d.costClass ||
      (orig.category ?? '') !== d.category ||
      (orig.subCategory ?? null) !== d.subCategory ||
      (orig.description ?? null) !== d.description ||
      (orig.code ?? null) !== (d.code ?? null) ||
      (orig.classCode ?? null) !== (d.classCode ?? null) ||
      (orig.revisedTotal ?? null) !== (d.revisedTotal ?? null);
    if (changed) {
      payload.push({
        id: d.id,
        cost_per_unit: d.costPerUnit,
        cost_class: d.costClass,
        category: d.category,
        sub_category: d.subCategory,
        description: d.description,
        code: d.code,
        class_code: d.classCode,
        revised_total: d.revisedTotal,
      });
    }
  });

  return payload;
};
